import React, { useState } from 'react';
import util from '../../styles/utilities.module.css';
import { useNavigate } from 'react-router-dom';
import { useSubmitImpactStoryMutation } from '../../slices/credentialingApi';
import './ImpactStoryPage.css';

const ImpactStoryPage = () => {
  const navigate = useNavigate();
  const [submitImpactStory, { isLoading }] = useSubmitImpactStoryMutation();
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    grade_level: 'K'
  });
  const [message, setMessage] = useState({ type: '', text: '' });
  const [isRedirecting, setIsRedirecting] = useState(false);

  const handleInputChange = (e) => {
    if (isRedirecting) return; // Prevent changes during redirect
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage({ type: '', text: '' });

    try {
  const { title, description, grade_level } = formData;
  const result = await submitImpactStory({ title, description, grade_level }).unwrap();
      
      if (result.success) {
        setMessage({
          type: 'success',
          text: `Impact story submitted successfully! You now have ${result.data.progress_update.stories_submitted} of ${result.data.progress_update.stories_target} stories submitted.`
        });
        
        setIsRedirecting(true);
        
        // Navigate to impact stories view page after 2 seconds to show success message
        setTimeout(() => {
          navigate('/credentialing/impact-stories');
        }, 2000);
      } else {
        setMessage({
          type: 'error',
          text: result.error || 'Failed to submit impact story. Please try again.'
        });
      }
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.data?.error || error.data?.message || 'Network error. Please check your connection and try again.'
      });
    }
  };

  return (
    <div className="impact-story-page">
      {/* Submitting overlay */}
      {(isLoading || isRedirecting) && (
        <div className="submitting-overlay">
          <div className="submitting-content">
            <div className="submitting-spinner"></div>
            <h3>
              {isRedirecting ? 'Success! Redirecting...' : 'Submitting Your Impact Story'}
            </h3>
            <p>
              {isRedirecting 
                ? 'Your impact story has been saved successfully! Redirecting to view your stories...'
                : 'Please wait while we save your story...'
              }
            </p>
          </div>
        </div>
      )}
      
      <div className="impact-story-container">
        <div className="impact-story-header">
          <button 
            className={`${util.btn} ${util['btn--tint']} ${util['btn-sm']} back-button`}
            type="button"
            onClick={() => navigate('/credentialing')}
          >
            ← Back to Training
          </button>
          <h1 className="impact-story-title">Share your story with us</h1>
          <p className="impact-story-subtitle">
            We’d love to hear how you’ve used ClassGenius in your classroom! Please share your impact story by answering the questions below.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="impact-story-form">
          <div className="form-group">
            <label htmlFor="title">
              Story Title *
            </label>
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              placeholder="e.g., Improved Reading Comprehension with AI-Generated Questions"
              disabled={isLoading || isRedirecting}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="grade_level">
              Grade Level *
            </label>
            <select
              id="grade_level"
              name="grade_level"
              value={formData.grade_level}
              onChange={handleInputChange}
              disabled={isLoading || isRedirecting}
              required
            >
              <option value="K">Kindergarten</option>
              <option value="1">1st Grade</option>
              <option value="2">2nd Grade</option>
              <option value="3">3rd Grade</option>
              <option value="4">4th Grade</option>
              <option value="5">5th Grade</option>
              <option value="6">6th Grade</option>
              <option value="7">7th Grade</option>
              <option value="8">8th Grade</option>
              <option value="9">9th Grade</option>
              <option value="10">10th Grade</option>
              <option value="11">11th Grade</option>
              <option value="12">12th Grade</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="description">
              Impact Story *
            </label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              placeholder={`Which development area or skill did you focus on with this assignment or activity?\nWhat inspired you to design this assignment or activity?\nWas there a specific behavior, skill, or knowledge gap you were aiming to address?\nWhat did you observe in your students’ learning, engagement, or performance?\nAny other observations, surprises, or lessons learned that you’d like to share?`}
              disabled={isLoading || isRedirecting}
              rows="8" /* Increased to ensure placeholder fits without scroll */
              required
            />
            <small className="char-count">
              {formData.description.length} characters (minimum 250 required)
            </small>
          </div>

          {message.text && (
            <div className={`submission-message ${message.type}`}>
              {message.text}
            </div>
          )}

          <div className="form-actions">
            <button
              type="submit"
              className={`${util.btn} ${util['btn--accentGradient']} ${util.elevateHover}`}
              disabled={isLoading || isRedirecting}
            >
              {isRedirecting ? 'Redirecting...' : isLoading ? 'Submitting...' : '📝 Submit Impact Story'}
            </button>
            <button
              type="button"
              className={`${util.btn} ${util['btn--ghost']}`}
              onClick={() => navigate('/credentialing')}
              disabled={isLoading || isRedirecting}
            >
              Cancel
            </button>
          </div>
        </form>

        <div className="impact-story-help-section">
          <h3>💡 Tips for Writing Your Impact Story</h3>
          <ul>
            <li><strong>Be Specific:</strong> Include concrete examples of how you used ClassGenius tools. When possible, share measurable outcomes.</li>
            <li><strong>Focus on Students:</strong> Highlight the improvements you observed in student learning</li>
            <li><strong>Be Authentic:</strong> You can share both successes and challenges</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default ImpactStoryPage;
